import type { QueryClient } from "@tanstack/react-query";
import type {
  ActiveErrorDto,
  CalibrationRunDto,
  CommandDto,
  ConfigDeploymentDto,
  ControlOwnerDto,
  MapImportDto,
  MissionDto,
  Page,
  RobotDto,
  Severity,
  TelemetryValueDto,
} from "@tcr/contracts";
import { qk } from "@/api/keys";
import { mergeMapImport } from "@/api/mapImports";
import type { RealtimeEvent } from "./manager";
import { useRealtimeStore, type TeleopStatus } from "./store";

const SEVERITY_RANK: Record<Severity, number> = { info: 0, warning: 1, error: 2, critical: 3 };

export function highestSeverity(errors: readonly ActiveErrorDto[]): Severity | null {
  let best: Severity | null = null;
  for (const e of errors) {
    const s = e.definition?.severity ?? "error"; // unknown codes are treated as errors
    if (best === null || SEVERITY_RANK[s] > SEVERITY_RANK[best]) best = s;
  }
  return best;
}

function robotIdFromTopic(topic: string): string | null {
  return topic.startsWith("robot:") ? topic.slice("robot:".length) : null;
}

function asArray<T>(data: unknown, key: string): T[] {
  if (Array.isArray(data)) return data as T[];
  if (data && typeof data === "object") {
    const inner = (data as Record<string, unknown>)[key];
    if (Array.isArray(inner)) return inner as T[];
    return [data as T];
  }
  return [];
}

function upsert<T extends { id: string }>(list: T[] | undefined, item: T, prepend = true): T[] | undefined {
  if (!list) return list;
  const i = list.findIndex((x) => x.id === item.id);
  if (i === -1) return prepend ? [item, ...list] : [...list, item];
  const next = list.slice();
  next[i] = { ...list[i]!, ...item };
  return next;
}

/** Merge a partial robot into every cache holding it. Returns false if any list lacked it. */
function patchRobot(qc: QueryClient, id: string, patch: Partial<RobotDto>): boolean {
  qc.setQueryData<RobotDto>(qk.robot(id), (r) => (r ? { ...r, ...patch } : r));
  let foundEverywhere = true;
  qc.setQueriesData<RobotDto[]>({ queryKey: qk.robotsAll }, (list) => {
    if (!Array.isArray(list)) return list;
    const i = list.findIndex((r) => r.id === id);
    if (i === -1) {
      foundEverywhere = false;
      return list;
    }
    const next = list.slice();
    next[i] = { ...list[i]!, ...patch };
    return next;
  });
  return foundEverywhere;
}

/**
 * Apply one realtime event to the TanStack Query cache (server state) or the
 * zustand store (ephemeral state). Event payload shapes follow the DTOs in
 * @tcr/contracts; small shape variations (array vs wrapper object) are tolerated.
 */
export function applyRealtimeEvent(qc: QueryClient, ev: RealtimeEvent): void {
  const topicRobot = robotIdFromTopic(ev.topic);
  const data = ev.data as Record<string, unknown> | null;
  const dataRobot = (data && typeof data === "object" && (typeof data.robotId === "string" ? data.robotId : null)) || null;

  // Not (yet) in RealtimeEventKind in @tcr/contracts; compared as a string.
  if (ev.kind === "map_import.updated") {
    const imp = data as unknown as MapImportDto | null;
    const robotId = imp?.robotId ?? topicRobot;
    if (imp?.id && robotId) mergeMapImport(qc, { ...imp, robotId });
    return;
  }

  switch (ev.kind) {
    case "robot.updated": {
      const robot = data as Partial<RobotDto> | null;
      const id = robot?.id ?? topicRobot;
      if (!id || !robot) return;
      const found = patchRobot(qc, id, robot);
      if (!found && ev.topic === "fleet") void qc.invalidateQueries({ queryKey: qk.robotsAll });
      return;
    }
    case "robot.telemetry": {
      const id = topicRobot ?? dataRobot;
      if (!id) return;
      const samples = asArray<TelemetryValueDto>(ev.data, "samples");
      qc.setQueryData<TelemetryValueDto[]>(qk.robotTelemetry(id), (list) => {
        const next = list ? list.slice() : [];
        for (const s of samples) {
          if (!s || typeof s.interfaceId !== "string") continue;
          const i = next.findIndex((x) => x.interfaceId === s.interfaceId);
          if (i === -1) next.push(s);
          else next[i] = { ...next[i]!, ...s };
        }
        return next;
      });
      return;
    }
    case "robot.errors": {
      const id = topicRobot ?? dataRobot;
      if (!id) return;
      const errors = asArray<ActiveErrorDto>(ev.data, "errors").filter((e) => e && typeof e.code === "string");
      qc.setQueryData(qk.robotErrors(id), errors);
      patchRobot(qc, id, { activeErrorCount: errors.length, highestSeverity: highestSeverity(errors) });
      return;
    }
    case "control.changed": {
      const id = topicRobot ?? dataRobot;
      if (!id) return;
      let control: ControlOwnerDto | null = null;
      if (data && typeof data === "object") {
        if ("control" in data) control = (data.control as ControlOwnerDto | null) ?? null;
        else if ("leaseId" in data && !(data as { releasedAt?: string | null }).releasedAt) control = data as unknown as ControlOwnerDto;
      }
      qc.setQueryData(qk.robotControl(id), control);
      const patch: Partial<RobotDto> = { control };
      const authority = data && typeof data === "object" && "authority" in data ? (data.authority as RobotDto["authority"] | undefined) : undefined;
      if (authority && typeof authority.generation === "number") {
        patch.authority = authority;
      } else if (control && typeof control.authorityGeneration === "number") {
        // Derive what we can from the lease; the refetch below fills in the rest.
        const prev = qc.getQueryData<RobotDto>(qk.robot(id))?.authority;
        const installed = control.state === "active";
        patch.authority = {
          generation: Math.max(control.authorityGeneration, prev?.generation ?? 0),
          installedGeneration: installed ? control.authorityGeneration : (prev?.installedGeneration ?? 0),
          mode: control.mode,
          pending: !installed,
        };
      }
      patchRobot(qc, id, patch);
      // Release/expiry install an unowned generation the event may not describe; keep RobotDto.authority fresh.
      void qc.invalidateQueries({ queryKey: qk.robot(id), exact: true });
      return;
    }
    case "command.updated": {
      const cmd = data as unknown as CommandDto | null;
      if (!cmd?.id) return;
      const id = cmd.robotId ?? topicRobot;
      if (id) qc.setQueryData<CommandDto[]>(qk.robotCommands(id), (l) => upsert(l, cmd));
      if (id) {
        // Keep the robot's "outcome unresolved" block current; the server picks the next one if several.
        const robot = qc.getQueryData<RobotDto>(qk.robot(id));
        if (cmd.status === "unknown" && !robot?.unresolved) {
          patchRobot(qc, id, {
            unresolved: { commandId: cmd.id, missionId: cmd.missionId, purpose: "command", interfaceId: cmd.interfaceId, error: cmd.error, createdAt: cmd.createdAt },
          });
          void qc.invalidateQueries({ queryKey: qk.robot(id) });
        } else if (cmd.status !== "unknown" && robot?.unresolved?.commandId === cmd.id) {
          void qc.invalidateQueries({ queryKey: qk.robot(id) });
          void qc.invalidateQueries({ queryKey: qk.robotsAll });
        }
      }
      // AUTH.* rejections mean the robot's installed authority differs from what we show.
      if (id && cmd.status === "rejected" && cmd.error?.code?.startsWith("AUTH.")) void qc.invalidateQueries({ queryKey: qk.robot(id), exact: true });
      if (cmd.missionId) {
        qc.setQueryData<MissionDto>(qk.mission(cmd.missionId), (m) => (m ? { ...m, commands: upsert(m.commands ?? [], cmd, false) } : m));
      }
      return;
    }
    case "mission.updated": {
      const m = data as unknown as MissionDto | null;
      if (!m?.id) return;
      qc.setQueryData<MissionDto>(qk.mission(m.id), (old) => (old ? { ...old, ...m, commands: m.commands ?? old.commands, events: m.events ?? old.events } : old));
      let found = false;
      qc.setQueriesData<Page<MissionDto>>({ queryKey: qk.missionsAll }, (page) => {
        if (!page || !Array.isArray(page.items)) return page;
        const i = page.items.findIndex((x) => x.id === m.id);
        if (i === -1) return page;
        found = true;
        const items = page.items.slice();
        items[i] = { ...page.items[i]!, ...m };
        return { ...page, items };
      });
      if (!found) void qc.invalidateQueries({ queryKey: qk.missionsAll });
      return;
    }
    case "calibration.updated": {
      const run = data as unknown as CalibrationRunDto | null;
      const id = run?.robotId ?? topicRobot;
      if (!run?.id || !id) return;
      qc.setQueryData<CalibrationRunDto[]>(qk.robotCalibrations(id), (l) => upsert(l, run));
      return;
    }
    case "deployment.updated": {
      const d = data as unknown as ConfigDeploymentDto | null;
      const id = d?.robotId ?? topicRobot;
      if (!d?.id || !id) return;
      qc.setQueryData<ConfigDeploymentDto[]>(qk.robotDeployments(id), (l) => upsert(l, d));
      if (d.status === "synced") patchRobot(qc, id, { configVersion: d.bundleVersion });
      return;
    }
    case "teleop.status": {
      const id = topicRobot ?? dataRobot;
      if (!id || !data) return;
      useRealtimeStore.getState().setTeleop(id, { ...(data as unknown as Omit<TeleopStatus, "at">), at: ev.at });
      return;
    }
    default:
      return;
  }
}
